import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRestoreJobPolling } from '../hooks/useRestoreJobPolling';

import { BackupFileBrowser } from '@features/backups/components/BackupFileBrowser';
import { FolderBrowserModal } from '@features/backups/components/FolderBrowserModal';
import {
  FolderOpen,
  FolderTree,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  FileCheck,
  X,
  Database,
} from 'lucide-react';

interface SelectiveRestoreWizardProps {
  isOpen: boolean;
  onClose: () => void;
  backupJobId: string;
  backupConfigName: string;
  backupConfigType?: 'FILES' | 'POSTGRESQL';
  sourceDatabase?: string;
  sourceHost?: string;
  sourcePort?: number;
  sourceUsername?: string;
  backupJobSnapshotId?: string | null;
  agentId?: string;
  initialSelectedFiles?: string[];
  onSelectedFilesChange?: (files: string[]) => void;
  onSuccess?: () => void;
}

type WizardStep = 'select-files' | 'configure' | 'preview' | 'progress' | 'complete';
type RestoreMode = 'file' | 'direct';
const NO_SELECTED_FILES: string[] = [];

interface PostgreSQLConfig {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  useBackupPassword: boolean;
}

interface RestoreConfig {
  location: 'original' | 'custom';
  customPath: string;
  overwrite: boolean;
  verifyAfterRestore: boolean;
  restoreMode: RestoreMode;
  postgresql: PostgreSQLConfig;
}

export const SelectiveRestoreWizard = ({
  isOpen,
  onClose,
  backupJobId,
  backupConfigName,
  backupConfigType = 'FILES',
  sourceDatabase = '',
  sourceHost = '',
  sourcePort = 5432,
  sourceUsername = '',
  backupJobSnapshotId: _backupJobSnapshotId,
  agentId,
  initialSelectedFiles,
  onSelectedFilesChange,
  onSuccess,
}: SelectiveRestoreWizardProps) => {
  const { t } = useTranslation('restores');
  const preselectedFiles = initialSelectedFiles ?? NO_SELECTED_FILES;
  const preselectedFilesKey = JSON.stringify(preselectedFiles);
  const initialStep: WizardStep = preselectedFiles.length > 0 ? 'configure' : 'select-files';
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [selectedFiles, setSelectedFiles] = useState<string[]>(() => [...preselectedFiles]);
  const [config, setConfig] = useState<RestoreConfig>({
    location: 'original',
    customPath: '',
    overwrite: false,
    verifyAfterRestore: true,
    restoreMode: 'file',
    postgresql: {
      host: sourceHost || '',
      port: sourcePort || 5432,
      database: sourceDatabase || '',
      username: sourceUsername || '',
      password: '',
      useBackupPassword: true,
    },
  });
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  const isPostgreSQL = backupConfigType === 'POSTGRESQL';
  const isDirectMode = config.restoreMode === 'direct';
  const isFileMode = config.restoreMode === 'file';
  const isOriginalServer = config.location === 'original';
  const isSeparateServer = config.location === 'custom';

  const {
    createRestoreJob,
    status,
    progress,
    currentFile,
    filesRestored,
    dirsCreated,
    totalFiles,
    bytesWritten,
    error,
    reset,
  } = useRestoreJobPolling();

  // Initialize from the parent only when the modal opens. Parent rerenders or
  // refreshed selection props must not replace choices made inside the wizard.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const files = JSON.parse(preselectedFilesKey) as string[];
      setStep(files.length > 0 ? 'configure' : 'select-files');
      setSelectedFiles(files);
      setConfig({
        location: 'original',
        customPath: '',
        overwrite: false,
        verifyAfterRestore: true,
        restoreMode: 'file',
        postgresql: {
          host: sourceHost || '',
          port: sourcePort || 5432,
          database: sourceDatabase || '',
          username: sourceUsername || '',
          password: '',
          useBackupPassword: true,
        },
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, preselectedFilesKey, sourceDatabase, sourceHost, sourcePort, sourceUsername]);

  const handleClose = useCallback(() => {
    reset();
    setStep('select-files');
    setSelectedFiles([]);
    setConfig({
      location: 'original',
      customPath: '',
      overwrite: false,
      verifyAfterRestore: true,
      restoreMode: 'file',
      postgresql: {
        host: sourceHost || '',
        port: sourcePort || 5432,
        database: sourceDatabase || '',
        username: sourceUsername || '',
        password: '',
        useBackupPassword: true,
      },
    });
    onClose();
  }, [onClose, reset, sourceDatabase, sourceHost, sourcePort, sourceUsername]);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusFrame = requestAnimationFrame(() => {
      document.getElementById('selective-restore-close')?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      openerRef.current?.focus();
      openerRef.current = null;
    };
  }, [isOpen]);

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleFileSelection = useCallback(
    (files: string[]) => {
      setSelectedFiles(files);
      onSelectedFilesChange?.(files);
      setStep('configure');
      setShowFileBrowser(false);
    },
    [onSelectedFilesChange]
  );

  const handleStartRestore = useCallback(async () => {
    setStep('progress');

    // Determine restore location and custom path based on backup type
    let restoreLocation: 'original' | 'custom';
    let customPath: string | undefined;

    if (isPostgreSQL) {
      // PostgreSQL backups
      if (isFileMode) {
        // Restore to file: always custom location with path
        restoreLocation = 'custom';
        customPath = config.customPath;
      } else {
        // Direct restore: use user's choice
        restoreLocation = config.location;
        customPath = isSeparateServer ? config.customPath : undefined;
      }
    } else {
      // Regular file backups: use user's choice
      restoreLocation = config.location;
      customPath = config.location === 'custom' ? config.customPath : undefined;
    }

    const request: Parameters<typeof createRestoreJob>[0] = {
      backup_job_id: backupJobId,
      restore_location: restoreLocation,
      custom_path: customPath,
      overwrite: config.overwrite,
      verify: config.verifyAfterRestore,
      selected_files: selectedFiles.length > 0 ? selectedFiles : undefined,
    };

    // Add restore mode for PostgreSQL backups
    if (isPostgreSQL) {
      request.restore_mode = config.restoreMode;

      // Add PostgreSQL config for direct mode
      if (isDirectMode) {
        request.postgresql = {
          host: config.postgresql.host,
          port: config.postgresql.port,
          database: config.postgresql.database,
          username: config.postgresql.username,
          password: config.postgresql.useBackupPassword ? '' : config.postgresql.password,
        };
      }
    }

    const jobId = await createRestoreJob(request);

    if (!jobId) {
      // Error handled by hook
      return;
    }
  }, [
    backupJobId,
    config,
    createRestoreJob,
    selectedFiles,
    isPostgreSQL,
    isDirectMode,
    isFileMode,
    isSeparateServer,
  ]);

  // Handle server location change
  const handleServerLocationChange = (location: 'original' | 'custom') => {
    setConfig((prev) => ({ ...prev, location }));

    if (location === 'original') {
      // Reset to original values
      setConfig((prev) => ({
        ...prev,
        location,
        postgresql: {
          host: sourceHost || '',
          port: sourcePort || 5432,
          database: sourceDatabase || '',
          username: sourceUsername || '',
          password: '',
          useBackupPassword: true,
        },
      }));
    } else {
      // Clear all fields for separate server
      setConfig((prev) => ({
        ...prev,
        location,
        postgresql: {
          host: '',
          port: 5432,
          database: '',
          username: '',
          password: '',
          useBackupPassword: false,
        },
      }));
    }
  };

  // Get restore mode description
  const getRestoreModeDescription = (mode: RestoreMode) => {
    switch (mode) {
      case 'file':
        return t('selective.configure.restoreToFileDesc');
      case 'direct':
        return t('selective.configure.directToDatabaseDesc');
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Wizard Modal */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="selective-restore-title"
          tabIndex={-1}
          onKeyDown={handleDialogKeyDown}
          className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col border border-surface-border-light dark:border-surface-border-dark overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-surface-border-light dark:border-surface-border-dark bg-accent-soft-light dark:bg-accent-soft-dark">
            <div className="flex items-center justify-between">
              <div>
                <h2
                  id="selective-restore-title"
                  className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase"
                >
                  {t('selective.title')}
                </h2>
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
                  {t('selective.from')}{' '}
                  <span className="text-brand-600 dark:text-brand-400 font-bold">
                    {backupConfigName}
                  </span>
                  {isPostgreSQL && (
                    <span className="ml-2 text-xs bg-state-info-bg-light dark:bg-state-info-bg-dark text-state-info-light dark:text-state-info-dark px-2 py-0.5 rounded-full">
                      {t('selective.postgreSQL')}
                    </span>
                  )}
                </p>
              </div>
              <button
                id="selective-restore-close"
                onClick={handleClose}
                aria-label={t('selective.actions.close')}
                className="min-h-11 min-w-11 p-2 text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-200 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="mt-6 flex items-center gap-2">
              {[
                { id: 'select-files', label: t('selective.steps.selectFiles'), icon: FolderTree },
                { id: 'configure', label: t('selective.steps.configure'), icon: HardDrive },
                { id: 'preview', label: t('selective.steps.preview'), icon: FileCheck },
                { id: 'progress', label: t('selective.steps.restore'), icon: CheckCircle2 },
              ].map((s, idx) => {
                const isActive = step === s.id;
                const isPast =
                  ['configure', 'preview', 'progress'].includes(step) &&
                  ['select-files', 'configure', 'preview'].indexOf(s.id) <
                    ['select-files', 'configure', 'preview'].indexOf(step);
                const Icon = s.icon;

                return (
                  <div key={s.id} className="flex items-center">
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${
                        isActive
                          ? 'bg-brand-600 text-white shadow-lg shadow-brand-200'
                          : isPast
                            ? 'bg-state-ok-bg-light text-state-ok-light dark:bg-state-ok-bg-dark dark:text-state-ok-dark'
                            : 'bg-surface-2-light text-text-subtle-light dark:text-text-subtle-dark dark:bg-surface-2-dark'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </div>
                    {idx < 3 && (
                      <div
                        className={`w-8 h-0.5 mx-1 ${isPast ? 'bg-state-ok-light/30 dark:bg-state-ok-dark/30' : 'bg-surface-border-light dark:bg-surface-border-dark'}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Select Files */}
            {step === 'select-files' && (
              <div className="space-y-6">
                <div className="bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-accent-soft-light dark:bg-accent-soft-dark rounded-xl">
                      <FolderTree className="w-6 h-6 text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-lg">
                        {t('selective.selectFiles.chooseTitle')}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-text-subtle-dark mt-1">
                        {t('selective.selectFiles.chooseDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowFileBrowser(true)}
                    className="group p-6 border-2 border-dashed border-border-strong-light dark:border-border-strong-dark rounded-xl hover:border-brand-600 dark:hover:border-brand-400 hover:bg-accent-soft-light dark:hover:bg-accent-soft-dark transition-all text-left"
                  >
                    <FolderOpen className="w-10 h-10 text-text-subtle-light dark:text-text-subtle-dark group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors mb-3" />
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      {t('selective.selectFiles.browseAndSelect')}
                    </h4>
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('selective.selectFiles.browseAndSelectDesc')}
                    </p>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedFiles([]);
                      onSelectedFilesChange?.([]);
                      setStep('configure');
                    }}
                    className="group p-6 border-2 border-dashed border-border-strong-light dark:border-border-strong-dark rounded-xl hover:border-state-ok-light dark:hover:border-state-ok-dark hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark transition-all text-left"
                  >
                    <HardDrive className="w-10 h-10 text-text-subtle-light dark:text-text-subtle-dark group-hover:text-state-ok-light dark:group-hover:text-state-ok-dark transition-colors mb-3" />
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      {t('selective.selectFiles.fullRestore')}
                    </h4>
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('selective.selectFiles.fullRestoreDesc')}
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Configure */}
            {step === 'configure' && (
              <div className="space-y-6">
                {selectedFiles.length > 0 && (
                  <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-state-ok-light dark:text-state-ok-dark" />
                        <span className="font-bold text-state-ok-light dark:text-state-ok-dark">
                          {t('selective.configure.itemsSelected', { count: selectedFiles.length })}
                        </span>
                      </div>
                      <button
                        onClick={() => setShowFileBrowser(true)}
                        className="text-sm text-state-ok-light dark:text-state-ok-dark hover:text-state-ok-light dark:hover:text-state-ok-dark font-medium"
                      >
                        {t('selective.configure.changeSelection')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Restore Mode Selection - Only for PostgreSQL */}
                {isPostgreSQL && (
                  <div className="bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl p-4">
                    <label className="block text-xs font-black uppercase tracking-widest text-state-info-light dark:text-state-info-dark mb-3">
                      <Database className="w-4 h-4 inline mr-1" />
                      {t('selective.configure.restoreMode')}
                    </label>
                    <div className="space-y-3">
                      <label
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                          config.restoreMode === 'file'
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="restoreMode"
                          value="file"
                          checked={config.restoreMode === 'file'}
                          onChange={(e) =>
                            setConfig({ ...config, restoreMode: e.target.value as RestoreMode })
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5"
                        />
                        <div className="ml-3">
                          <div className="font-bold text-slate-900 dark:text-white text-sm">
                            {t('selective.configure.restoreToFile')}
                          </div>
                          <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {getRestoreModeDescription('file')}
                          </div>
                        </div>
                      </label>

                      <label
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                          config.restoreMode === 'direct'
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="restoreMode"
                          value="direct"
                          checked={config.restoreMode === 'direct'}
                          onChange={(e) =>
                            setConfig({ ...config, restoreMode: e.target.value as RestoreMode })
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5"
                        />
                        <div className="ml-3">
                          <div className="font-bold text-slate-900 dark:text-white text-sm">
                            {t('selective.configure.directToDatabase')}
                          </div>
                          <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {getRestoreModeDescription('direct')}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* File Mode - Custom Path Only */}
                {isPostgreSQL && isFileMode && (
                  <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-4">
                    <label className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3">
                      {t('selective.configure.customPathForDump')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={config.customPath}
                        onChange={(e) => setConfig({ ...config, customPath: e.target.value })}
                        placeholder={t('selective.configure.customPathPlaceholder')}
                        className="flex-1 px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                      />
                      {agentId && (
                        <button
                          type="button"
                          onClick={() => setShowFolderBrowser(true)}
                          className="px-3 py-2 bg-surface-border-light dark:bg-surface-border-dark border border-border-strong-light dark:border-border-strong-dark rounded-lg text-slate-700 dark:text-slate-300 hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors font-medium flex items-center gap-2"
                          title={t('selective.configure.browseFolders')}
                        >
                          <FolderOpen className="w-4 h-4" />
                          <span className="hidden sm:inline">
                            {t('selective.configure.browse')}
                          </span>
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-2">
                      {t('selective.configure.customPathHint')}
                    </p>
                  </div>
                )}

                {/* PostgreSQL Server Selection - Only for Direct Mode */}
                {isDirectMode && (
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3">
                      {t('selective.configure.pgServer')}
                    </label>
                    <div className="space-y-3">
                      <label
                        className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          isOriginalServer
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="serverLocation"
                          value="original"
                          checked={isOriginalServer}
                          onChange={(e) =>
                            handleServerLocationChange(e.target.value as 'original' | 'custom')
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400"
                        />
                        <div className="ml-4">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {t('selective.configure.originalDbServer')}
                          </div>
                          <div className="text-sm text-text-muted-light dark:text-text-muted-dark">
                            {t('selective.configure.originalDbServerDesc')}
                          </div>
                        </div>
                      </label>
                      <label
                        className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          isSeparateServer
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="serverLocation"
                          value="custom"
                          checked={isSeparateServer}
                          onChange={(e) =>
                            handleServerLocationChange(e.target.value as 'original' | 'custom')
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400"
                        />
                        <div className="ml-4">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {t('selective.configure.separateServer')}
                          </div>
                          <div className="text-sm text-text-muted-light dark:text-text-muted-dark">
                            {t('selective.configure.separateServerDesc')}
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Overwrite Warning for Original Server */}
                    {isOriginalServer && (
                      <div className="mt-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-state-warn-light dark:text-state-warn-dark flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-state-warn-light dark:text-state-warn-dark">
                              {t('selective.configure.dbOverwriteWarning')}
                            </h4>
                            <p className="text-sm text-state-warn-light dark:text-state-warn-dark mt-1">
                              {t('selective.configure.dbOverwriteWarningDesc')}
                            </p>
                            <label className="flex items-center mt-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={config.overwrite}
                                onChange={(e) =>
                                  setConfig({ ...config, overwrite: e.target.checked })
                                }
                                className="w-4 h-4 text-state-warn-light dark:text-state-warn-dark rounded border-state-warn-light dark:border-state-warn-dark"
                              />
                              <span className="ml-2 text-sm font-bold text-state-warn-light dark:text-state-warn-dark">
                                {t('selective.configure.confirmOverwrite')}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PostgreSQL Connection Config - Below Server Selection */}
                    <div className="mt-6 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl p-4">
                      <label className="block text-xs font-black uppercase tracking-widest text-state-info-light dark:text-state-info-dark mb-4">
                        {t('selective.configure.pgConnection')}
                        {isOriginalServer && (
                          <span className="ml-2 text-xs font-normal text-text-muted-light dark:text-text-muted-dark">
                            {t('selective.configure.pgConnectionReadOnly')}
                          </span>
                        )}
                        {isSeparateServer && (
                          <span className="ml-2 text-xs font-normal text-state-warn-light dark:text-state-warn-dark">
                            {t('selective.configure.pgConnectionNewServer')}
                          </span>
                        )}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                            {t('selective.configure.host')}{' '}
                            <span className="text-state-danger-light dark:text-state-danger-dark">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            value={config.postgresql.host}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                postgresql: { ...prev.postgresql, host: e.target.value },
                              }))
                            }
                            placeholder={
                              isSeparateServer ? t('selective.configure.enterHost') : 'localhost'
                            }
                            disabled={isOriginalServer}
                            className="w-full px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                            {t('selective.configure.port')}
                          </label>
                          <input
                            type="number"
                            value={config.postgresql.port}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                postgresql: {
                                  ...prev.postgresql,
                                  port: parseInt(e.target.value) || 5432,
                                },
                              }))
                            }
                            placeholder="5432"
                            disabled={isOriginalServer}
                            className="w-full px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                            {t('selective.configure.database')}{' '}
                            <span className="text-state-danger-light dark:text-state-danger-dark">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            value={config.postgresql.database}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                postgresql: { ...prev.postgresql, database: e.target.value },
                              }))
                            }
                            placeholder={
                              isSeparateServer
                                ? t('selective.configure.enterDatabase')
                                : 'mydatabase'
                            }
                            disabled={isOriginalServer}
                            className="w-full px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                            {t('selective.configure.username')}{' '}
                            <span className="text-state-danger-light dark:text-state-danger-dark">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            value={config.postgresql.username}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                postgresql: { ...prev.postgresql, username: e.target.value },
                              }))
                            }
                            placeholder={
                              isSeparateServer ? t('selective.configure.enterUsername') : 'postgres'
                            }
                            disabled={isOriginalServer}
                            className="w-full px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                            {t('selective.configure.password')}
                            {config.postgresql.useBackupPassword && isOriginalServer && (
                              <span className="ml-2 text-xs font-normal text-state-ok-light dark:text-state-ok-dark">
                                {t('selective.configure.usingBackupPassword')}
                              </span>
                            )}
                          </label>
                          {isOriginalServer && config.postgresql.useBackupPassword ? (
                            <button
                              type="button"
                              onClick={() =>
                                setConfig((prev) => ({
                                  ...prev,
                                  postgresql: { ...prev.postgresql, useBackupPassword: false },
                                }))
                              }
                              className="w-full px-3 py-2 bg-surface-2-light dark:bg-surface-border-dark border border-dashed border-surface-border-light dark:border-border-strong-dark rounded-lg text-slate-600 dark:text-text-subtle-dark text-sm font-medium hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-all text-left"
                            >
                              {t('selective.configure.clickToChangePassword')}
                            </button>
                          ) : (
                            <input
                              type="password"
                              value={config.postgresql.password}
                              onChange={(e) =>
                                setConfig((prev) => ({
                                  ...prev,
                                  postgresql: { ...prev.postgresql, password: e.target.value },
                                }))
                              }
                              placeholder={
                                isSeparateServer
                                  ? t('selective.configure.enterPassword')
                                  : t('selective.configure.enterNewPassword')
                              }
                              className="w-full px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Restore Destination - Only for non-PostgreSQL */}
                {!isPostgreSQL && (
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3">
                      {t('selective.configure.restoreDestination')}
                    </label>
                    <div className="space-y-3">
                      <label
                        className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          config.location === 'original'
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="location"
                          value="original"
                          checked={config.location === 'original'}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              location: e.target.value as 'original' | 'custom',
                            })
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400"
                        />
                        <div className="ml-4">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {t('selective.configure.originalLocation')}
                          </div>
                          <div className="text-sm text-text-muted-light dark:text-text-muted-dark">
                            {t('selective.configure.originalLocationDesc')}
                          </div>
                        </div>
                      </label>

                      <label
                        className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          config.location === 'custom'
                            ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark'
                            : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                        }`}
                      >
                        <input
                          type="radio"
                          name="location"
                          value="custom"
                          checked={config.location === 'custom'}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              location: e.target.value as 'original' | 'custom',
                            })
                          }
                          className="w-4 h-4 text-brand-600 dark:text-brand-400"
                        />
                        <div className="ml-4 flex-1">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {t('selective.configure.customLocation')}
                          </div>
                          {config.location === 'custom' && (
                            <div className="mt-2 flex gap-2">
                              <input
                                type="text"
                                value={config.customPath}
                                onChange={(e) =>
                                  setConfig({ ...config, customPath: e.target.value })
                                }
                                placeholder={t('selective.configure.customLocationPlaceholder')}
                                className="flex-1 px-3 py-2 text-sm border border-border-strong-light dark:border-border-strong-dark rounded-lg bg-surface-card-light dark:bg-surface-2-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                autoFocus
                              />
                              {agentId && (
                                <button
                                  type="button"
                                  onClick={() => setShowFolderBrowser(true)}
                                  className="px-3 py-2 bg-surface-border-light dark:bg-surface-border-dark border border-border-strong-light dark:border-border-strong-dark rounded-lg text-slate-700 dark:text-slate-300 hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors font-medium flex items-center gap-2"
                                  title={t('selective.configure.browseFolders')}
                                >
                                  <FolderOpen className="w-4 h-4" />
                                  <span className="hidden sm:inline">
                                    {t('selective.configure.browse')}
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Overwrite Warning for Original Location */}
                    {config.location === 'original' && (
                      <div className="mt-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-state-warn-light dark:text-state-warn-dark flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-state-warn-light dark:text-state-warn-dark">
                              {t('selective.configure.dataOverwriteWarning')}
                            </h4>
                            <p className="text-sm text-state-warn-light dark:text-state-warn-dark mt-1">
                              {t('selective.configure.dataOverwriteWarningDesc')}
                            </p>
                            <label className="flex items-center mt-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={config.overwrite}
                                onChange={(e) =>
                                  setConfig({ ...config, overwrite: e.target.checked })
                                }
                                className="w-4 h-4 text-state-warn-light dark:text-state-warn-dark rounded border-state-warn-light dark:border-state-warn-dark"
                              />
                              <span className="ml-2 text-sm font-bold text-state-warn-light dark:text-state-warn-dark">
                                {t('selective.configure.confirmOverwrite')}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Verification Option */}
                <label className="flex items-center p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.verifyAfterRestore}
                    onChange={(e) => setConfig({ ...config, verifyAfterRestore: e.target.checked })}
                    className="w-4 h-4 text-brand-600 dark:text-brand-400 rounded"
                  />
                  <div className="ml-3">
                    <div className="font-bold text-slate-900 dark:text-white">
                      {t('selective.configure.verifyAfterRestore')}
                    </div>
                    <div className="text-sm text-text-muted-light dark:text-text-muted-dark">
                      {t('selective.configure.verifyAfterRestoreDesc')}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Step 3: Preview */}
            {step === 'preview' && (
              <div className="space-y-6">
                <div className="bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl p-6">
                  <h3 className="font-bold text-state-info-light dark:text-state-info-dark text-lg mb-4">
                    {t('selective.preview.restoreSummary')}
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-surface-card-light dark:bg-surface-2-dark rounded-lg p-4">
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('selective.preview.selectedItems')}
                      </div>
                      <div className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                        {selectedFiles.length > 0
                          ? selectedFiles.length
                          : t('selective.preview.all')}
                      </div>
                    </div>
                    <div className="bg-surface-card-light dark:bg-surface-2-dark rounded-lg p-4">
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('selective.preview.destination')}
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white mt-1 truncate">
                        {isPostgreSQL
                          ? // PostgreSQL backups
                            isDirectMode
                            ? t('selective.configure.directToDatabase')
                            : config.customPath || t('selective.preview.notSet')
                          : // Regular file backups
                            config.location === 'original'
                            ? t('selective.configure.originalLocation')
                            : config.customPath || t('selective.preview.notSet')}
                      </div>
                    </div>
                  </div>

                  {isPostgreSQL && (
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div className="bg-surface-card-light dark:bg-surface-2-dark rounded-lg p-4">
                        <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                          {t('selective.preview.restoreMode')}
                        </div>
                        <div className="text-lg font-bold text-slate-900 dark:text-white mt-1 capitalize">
                          {config.restoreMode === 'file'
                            ? t('selective.configure.restoreToFile')
                            : t('selective.configure.directToDatabase')}
                        </div>
                      </div>
                      {isDirectMode && (
                        <div className="bg-surface-card-light dark:bg-surface-2-dark rounded-lg p-4">
                          <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('selective.preview.database')}
                          </div>
                          <div className="text-lg font-bold text-slate-900 dark:text-white mt-1 truncate">
                            {config.postgresql.database}@{config.postgresql.host}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-2">
                        {t('selective.preview.selectedPaths')}
                      </div>
                      <div className="bg-surface-card-light dark:bg-surface-2-dark rounded-lg p-3 max-h-32 overflow-y-auto">
                        {selectedFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="text-sm font-mono text-slate-700 dark:text-slate-300 py-1 truncate"
                          >
                            {file}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
                    <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                      {error}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Progress */}
            {step === 'progress' && (
              <div className="space-y-6">
                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                      {status === 'PENDING'
                        ? t('selective.progress.waitingToStart')
                        : status === 'RUNNING'
                          ? t('selective.progress.restoringFiles')
                          : status === 'COMPLETED'
                            ? t('selective.progress.restoreComplete')
                            : status === 'FAILED'
                              ? t('selective.progress.restoreFailed')
                              : t('selective.progress.processing')}
                    </span>
                    <span className="text-sm font-black text-brand-600 dark:text-brand-400">
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-brand-600 dark:bg-brand-400 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Current File */}
                {currentFile && (
                  <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-4">
                    <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1">
                      {t('selective.progress.currentFile')}
                    </div>
                    <div className="font-mono text-sm text-slate-700 dark:text-slate-300 truncate">
                      {currentFile}
                    </div>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-4 text-center">
                    <div className="text-2xl font-black text-slate-900 dark:text-white">
                      {filesRestored}
                      {totalFiles > 0 && (
                        <span className="text-text-subtle-light dark:text-text-subtle-dark text-lg">
                          /{totalFiles}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mt-1">
                      {t('selective.progress.files')}
                    </div>
                  </div>
                  <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-4 text-center">
                    <div className="text-2xl font-black text-slate-900 dark:text-white">
                      {bytesWritten
                        ? bytesWritten >= 1024 * 1024
                          ? `${(bytesWritten / 1024 / 1024).toFixed(1)} MB`
                          : bytesWritten >= 1024
                            ? `${(bytesWritten / 1024).toFixed(1)} KB`
                            : `${bytesWritten} B`
                        : '-'}
                    </div>
                    <div className="text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mt-1">
                      {t('selective.progress.transferred')}
                    </div>
                  </div>
                </div>

                {/* Status */}
                {status === 'COMPLETED' && (
                  <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-8 h-8 text-state-ok-light dark:text-state-ok-dark" />
                      <div>
                        <h4 className="font-bold text-state-ok-light dark:text-state-ok-dark">
                          {t('selective.progress.restoreCompleteTitle')}
                        </h4>
                        <p className="text-sm text-state-ok-light dark:text-state-ok-dark">
                          {dirsCreated > 0
                            ? t('selective.progress.filesRestoredSuccessWithDirs', {
                                count: filesRestored,
                                dirs: dirsCreated,
                              })
                            : t('selective.progress.filesRestoredSuccess', {
                                count: filesRestored,
                              })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {status === 'FAILED' && error && (
                  <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
                    <h4 className="font-bold text-state-danger-light dark:text-state-danger-dark">
                      {t('selective.progress.restoreFailedTitle')}
                    </h4>
                    <p className="text-sm text-state-danger-light dark:text-state-danger-dark mt-1">
                      {error}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-5 border-t border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
            <div className="flex justify-between">
              {step === 'select-files' && (
                <>
                  <button
                    onClick={handleClose}
                    className="min-h-11 px-6 py-2.5 text-sm font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    {t('selective.actions.cancel')}
                  </button>
                  <button
                    onClick={() => setStep('configure')}
                    className="min-h-11 px-6 py-2.5 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-xl hover:bg-brand-700 transition-colors"
                  >
                    {t('selective.actions.fullRestore')}{' '}
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </button>
                </>
              )}

              {step === 'configure' && (
                <>
                  <button
                    onClick={() => setStep('select-files')}
                    className="min-h-11 px-6 py-2.5 text-sm font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300 flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('selective.actions.back')}
                  </button>
                  <button
                    onClick={() => setStep('preview')}
                    disabled={
                      // PostgreSQL file mode: require custom path
                      (isPostgreSQL && isFileMode && !config.customPath) ||
                      // PostgreSQL direct mode: require connection details
                      (isPostgreSQL &&
                        isDirectMode &&
                        (!config.postgresql.host || !config.postgresql.username)) ||
                      (isPostgreSQL &&
                        isDirectMode &&
                        isSeparateServer &&
                        !config.postgresql.password) ||
                      (isPostgreSQL && isDirectMode && isOriginalServer && !config.overwrite) ||
                      // Regular file backup with custom location: require custom path
                      (!isPostgreSQL && config.location === 'custom' && !config.customPath) ||
                      // Regular file backup to original location: require overwrite confirmation
                      (!isPostgreSQL && config.location === 'original' && !config.overwrite)
                    }
                    className="min-h-11 px-6 py-2.5 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('selective.actions.preview')} <ArrowRight className="w-4 h-4 inline ml-2" />
                  </button>
                </>
              )}

              {step === 'preview' && (
                <>
                  <button
                    onClick={() => setStep('configure')}
                    className="min-h-11 px-6 py-2.5 text-sm font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300 flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('selective.actions.back')}
                  </button>
                  <button
                    onClick={handleStartRestore}
                    disabled={
                      // PostgreSQL direct to original: require overwrite
                      (isPostgreSQL && isDirectMode && isOriginalServer && !config.overwrite) ||
                      // Regular file backup to original: require overwrite
                      (!isPostgreSQL && config.location === 'original' && !config.overwrite)
                    }
                    className="min-h-11 px-6 py-2.5 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-50"
                  >
                    {t('selective.actions.startRestore')}
                  </button>
                </>
              )}

              {step === 'progress' && (
                <>
                  <div />
                  {status === 'COMPLETED' || status === 'FAILED' ? (
                    <button
                      onClick={() => {
                        onSuccess?.();
                        handleClose();
                      }}
                      className="min-h-11 px-6 py-2.5 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-xl hover:bg-brand-700 transition-colors"
                    >
                      {status === 'COMPLETED'
                        ? t('selective.actions.done')
                        : t('selective.actions.close')}
                    </button>
                  ) : (
                    <button
                      onClick={handleClose}
                      className="min-h-11 px-6 py-2.5 text-sm font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300"
                    >
                      {t('selective.actions.continueInBackground')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File Browser Modal */}
      {showFileBrowser && (
        <BackupFileBrowser
          isOpen={showFileBrowser}
          onClose={() => {
            setShowFileBrowser(false);
            if (selectedFiles.length > 0) {
              setStep('configure');
            }
          }}
          backupJobId={backupJobId}
          backupConfigName={backupConfigName}
          initialSelectedFiles={selectedFiles}
          onSelectFiles={handleFileSelection}
        />
      )}

      {/* Folder Browser Modal for Restore Path */}
      {showFolderBrowser && agentId && (
        <FolderBrowserModal
          isOpen={showFolderBrowser}
          onClose={() => setShowFolderBrowser(false)}
          agentId={agentId}
          initialPath={config.customPath}
          selectedPaths={config.customPath ? [config.customPath] : []}
          onSelectPaths={(paths) => {
            if (paths.length > 0) {
              setConfig({ ...config, customPath: paths[0] });
            }
          }}
          allowMultiple={false}
        />
      )}
    </>
  );
};
